import LZString from 'lz-string';
import logger from './logger';

// Generic LZ-String-compressed localStorage cache with an in-memory memo.
//
// Decompressing a multi-megabyte cache blocks the main thread for hundreds of
// milliseconds, so localStorage is only touched once per session per key: reads
// after the first (and all reads after a write) are served from memory.

export interface CompressedCache<T> {
  get: () => T[] | null;
  set: (items: T[]) => void;
  clear: () => void;
  shouldRefresh: () => boolean;
  getLastRefreshTime: () => Date | null;
  /** Restamp the refresh time without rewriting data — call when a sync
   *  verified the cache is already up to date. Without it, a no-change sync
   *  leaves shouldRefresh() true and staleness triggers loop forever. */
  touch: () => void;
}

interface CompressedCacheOptions<T> {
  storageKey: string;
  lastRefreshKey: string;
  refreshDelayMs: number;
  /** Strips an item down to the fields worth persisting. */
  slim?: (item: T) => Partial<T>;
}

export const createCompressedCache = <T>({
  storageKey,
  lastRefreshKey,
  refreshDelayMs,
  slim,
}: CompressedCacheOptions<T>): CompressedCache<T> => {
  let memo: T[] | null = null;
  let memoLoaded = false;

  const readFromStorage = (): T[] | null => {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;

    try {
      const decompressed = LZString.decompressFromUTF16(raw);
      if (!decompressed) throw new Error('decompression failed');
      return JSON.parse(decompressed);
    } catch {
      // Legacy plain-JSON format
      try {
        return JSON.parse(raw);
      } catch {
        logger.error(`Corrupt cache at ${storageKey}, clearing`);
        localStorage.removeItem(storageKey);
        localStorage.removeItem(lastRefreshKey);
        return null;
      }
    }
  };

  const get = (): T[] | null => {
    if (!memoLoaded) {
      memo = readFromStorage();
      memoLoaded = true;
    }
    return memo;
  };

  const set = (items: T[]): void => {
    const slimmed = slim ? items.map((item) => ({ ...slim(item) } as T)) : items;
    memo = slimmed;
    memoLoaded = true;

    try {
      const compressed = LZString.compressToUTF16(JSON.stringify(slimmed));
      const previous = localStorage.getItem(storageKey);
      try {
        localStorage.setItem(storageKey, compressed);
        localStorage.setItem(lastRefreshKey, Date.now().toString());
      } catch (error) {
        if (error instanceof DOMException && error.name === 'QuotaExceededError') {
          logger.warn(`Quota exceeded writing ${storageKey} (${compressed.length} chars)`);
          // Keep the old persisted cache rather than losing data; only replace
          // it when the new payload is smaller and might fit after clearing.
          if (previous && compressed.length < previous.length) {
            try {
              localStorage.removeItem(storageKey);
              localStorage.setItem(storageKey, compressed);
              localStorage.setItem(lastRefreshKey, Date.now().toString());
            } catch {
              try {
                localStorage.setItem(storageKey, previous);
              } catch {}
            }
          }
        } else {
          throw error;
        }
      }
    } catch (error) {
      // Persisting is best-effort; the in-memory copy keeps the app working.
      logger.error(`Error persisting cache ${storageKey}:`, error);
    }
  };

  const clear = (): void => {
    memo = null;
    memoLoaded = true;
    localStorage.removeItem(storageKey);
    localStorage.removeItem(lastRefreshKey);
  };

  const getLastRefreshTime = (): Date | null => {
    const raw = localStorage.getItem(lastRefreshKey);
    return raw ? new Date(parseInt(raw, 10)) : null;
  };

  const shouldRefresh = (): boolean => {
    const last = getLastRefreshTime();
    if (!last) return true;
    return Date.now() - last.getTime() > refreshDelayMs;
  };

  const touch = (): void => {
    try {
      localStorage.setItem(lastRefreshKey, Date.now().toString());
    } catch {}
  };

  return { get, set, clear, shouldRefresh, getLastRefreshTime, touch };
};
